import React from 'react';

import { useGetStaffUsersQuery } from '@/services/api/userService';
import { User } from '@/services/types/user-types';

import { ComboboxLabel, ComboboxOption } from '../../ui/catalyst/combobox';
import { ComboboxWithClear } from '../../ui/catalyst/combobox-with-clear';

interface AssignmentDropdownProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
}

// Common assignees - main ones are Lisbeth and Denise Biggs
const FREQUENT_ASSIGNEE_NAMES = [
  // Main assignees (most common)
  'Lisbeth',
  'Denise Biggs',
  // Other frequent assignees
  'Cole',
  'Michelle Pimienta',
  'Jacqueline',
  'Julie Lopez',
];

const AssignmentDropdownComponent: React.FC<AssignmentDropdownProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { data: staffUsers = [], isLoading } = useGetStaffUsersQuery();

  // Find the selected user or null if none selected
  const selectedUser = value
    ? staffUsers.find((user) => user.id === value) || null
    : null;

  // Create a Set to track frequent user IDs to avoid duplicates
  const frequentUserIds = new Set<string>();
  const frequentUsers: User[] = [];

  // Find frequent users by matching names (avoid duplicates)
  FREQUENT_ASSIGNEE_NAMES.forEach((name) => {
    const matchedUser = staffUsers.find((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const userName = user.username;
      // Match by full name, first name, or username
      return (
        fullName === name ||
        user.first_name === name ||
        userName === name ||
        fullName.includes(name) ||
        name.includes(fullName)
      );
    });

    if (matchedUser && !frequentUserIds.has(matchedUser.id)) {
      frequentUserIds.add(matchedUser.id);
      frequentUsers.push(matchedUser);
    }
  });

  // Get all other users, excluding the frequent ones
  const otherUsers = staffUsers
    .filter((user) => !frequentUserIds.has(user.id))
    .sort((a, b) => {
      const aName = `${a.first_name} ${a.last_name}`.trim() || a.username;
      const bName = `${b.first_name} ${b.last_name}`.trim() || b.username;
      return aName.localeCompare(bName);
    });

  const allUsers = [...frequentUsers, ...otherUsers];

  const displayValue = (user: User | null) => {
    if (!user) return '';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) {
      return user.first_name;
    }
    return user.username;
  };

  const handleSelectionChange = (user: User | null) => {
    onChange(user?.id || null);
  };

  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-gray-700">Assigned To</div>
      <ComboboxWithClear
        value={selectedUser}
        onChange={handleSelectionChange}
        options={allUsers}
        displayValue={displayValue}
        placeholder="Select a staff member"
        className="w-full text-sm [&>span>input]:py-1.5 [&>span>input]:text-sm"
        aria-label="Assign to staff member"
        disabled={disabled || isLoading}
        clearable
        onClear={() => onChange(null)}
      >
        {(user: User) => {
          const isFrequent = frequentUserIds.has(user.id);
          const displayName =
            user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.first_name || user.username;

          return (
            <ComboboxOption value={user} key={user.id}>
              <ComboboxLabel className={isFrequent ? 'font-medium' : ''}>
                {displayName}
                {isFrequent && (
                  <span className="ml-2 text-xs text-blue-600 group-data-focus/option:text-white group-data-selected/option:text-white">
                    ★ Frequent
                  </span>
                )}
              </ComboboxLabel>
            </ComboboxOption>
          );
        }}
      </ComboboxWithClear>
      <div className="flex items-center gap-2 mt-0.5">
        {!selectedUser && (
          <span className="text-xs text-gray-500">Currently unassigned</span>
        )}
      </div>
      {isLoading && <p className="text-xs text-gray-400">Loading staff...</p>}
    </div>
  );
};

export const AssignmentDropdown = React.memo(AssignmentDropdownComponent);
